import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as CloseIcon } from 'src/assets/svg/modal-close.svg'
import styles from './renameFacilityModal.module.scss'
import { AnalysisGoalPicker } from '../AnalysisGoalPicker'

interface Props {
  // '' when the facility has none
  currentCategory?: string
  currentName: string
  isOpen: boolean
  onClose: () => void
  // Only what changed; category '' clears it.
  onConfirm: (changes: { category?: string; name?: string }) => void
}

export const RenameFacilityModal: FC<Props> = ({
  currentCategory = '',
  currentName,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [name, setName] = useState(currentName)
  const [category, setCategory] = useState(currentCategory)

  useEffect(() => {
    if (isOpen) {
      setName(currentName)
      setCategory(currentCategory)
    }
  }, [isOpen, currentName, currentCategory])

  if (!isOpen) return null

  const trimmed = name.trim()
  const nameChanged = trimmed.length > 0 && trimmed !== currentName
  const categoryChanged = category !== currentCategory
  const canSave = trimmed.length > 0 && (nameChanged || categoryChanged)

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Edit facility</h2>
          <button className={styles.closeBtn} onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </div>

        <label className={styles.label} htmlFor="facilityRename">
          Facility Name
        </label>
        <input
          autoFocus
          className={styles.input}
          id="facilityRename"
          onChange={(e) => setName(e.target.value)}
          type="text"
          value={name}
        />

        <div className={styles.category}>
          <AnalysisGoalPicker compact onChange={setCategory} value={category} />
        </div>

        <div className={styles.footer}>
          <button className={styles.discardBtn} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={styles.saveBtn}
            disabled={!canSave}
            onClick={() =>
              onConfirm({
                ...(nameChanged ? { name: trimmed } : {}),
                ...(categoryChanged ? { category } : {}),
              })
            }
            type="button"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
